import { fetchFacilityDetail } from '@/entities/facility';
import { checkNullId, handleFetchDivisions, showMessageError } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import type { AutoCompleteAddressProps } from './autoCompleteAddress.types';

const initialAddress = {
  name: '',
  country: 'US',
  address: '',
  city: '',
  state: '',
  zip: '',
  phoneNumber: '',
  email: '',
};

/**
 * Manages the RMA shipping and returning address forms, including the same-as-shipping mirror.
 * Prefills from the facility contact when facilityId and countries are set or from the initial
 * addresses passed in, keeps the returning address cloned from shipping while mirrored, and pushes
 * every address and division change to the parent through onChangeDataAddress and onChangeDivision.
 *
 * @param props - Address props: countries, facilityId, onChangeDataAddress, onChangeDivision,
 *   isSameAsShippingAddress, initialShippingAddress and initialReturningAddress.
 * @returns An object carrying the address state and setter plus the key-down, selection, input,
 *   autocomplete, clear and field-change handlers.
 */
const useAutoCompleteAddress = ({
  countries,
  facilityId,
  onChangeDataAddress,
  onChangeDivision,
  isSameAsShippingAddress,
  initialShippingAddress,
  initialReturningAddress,
}: Pick<
  AutoCompleteAddressProps,
  | 'countries'
  | 'facilityId'
  | 'onChangeDataAddress'
  | 'onChangeDivision'
  | 'isSameAsShippingAddress'
  | 'initialShippingAddress'
  | 'initialReturningAddress'
>) => {
  const [state, setState] = useMergeState<any>({
    shippingAddressData: _.cloneDeep(initialAddress), // UI data
    shippingStateObject: null, // value use in state select input
    shippingCountryObject: null, // value use in country select input
    shippingDivisions: undefined, // state options when country is CA or US

    returningAddressData: _.cloneDeep(initialAddress),
    returningStateObject: null,
    returningCountryObject: null,
    returningDivisions: undefined,

    isSameAsShippingAddress,
    isLoadedData: false,
  });

  /**
   * Prefills the RMA shipping and returning addresses from the selected facility's contact. Fetches the facility, maps name, address, phone and the first notification email onto the shipping address, resolves the country option plus divisions to find the matching state for US or CA, then either copies shipping into the returning address when same-as-shipping is set or seeds the returning address on its own; errors are logged and shown.
   *
   * @returns Resolves after the prefilled addresses are stored.
   */
  const fetchFacilityData = async () => {
    const setStateObject = { isLoadedData: true };
    try {
      if (facilityId) {
        const facilityData = await fetchFacilityDetail(facilityId);
        if (facilityData?.contact) {
          const { contact, notificationEmail, name } = facilityData;

          // Take the first email from string notificationEmail
          const firstEmail = notificationEmail?.split(',')[0];

          const updatedShippingAddressData = { ...state.shippingAddressData };
          updatedShippingAddressData.name = name;
          updatedShippingAddressData.address = contact.address;
          updatedShippingAddressData.country = contact.country || 'US';
          updatedShippingAddressData.city = contact.city;
          updatedShippingAddressData.state = contact.state;
          updatedShippingAddressData.zip = contact.zip;
          updatedShippingAddressData.phoneNumber = contact.phone1;
          updatedShippingAddressData.email = firstEmail;
          const updatedReturningAddressData = _.cloneDeep(initialAddress);
          updatedReturningAddressData.name = name ?? '';
          let shippingCountryObject;
          if (contact.country === 'US' || contact.country === 'CA') {
            shippingCountryObject = {
              label: contact.country === 'US' ? 'United States' : 'Canada',
              value: contact.country,
            };
            _.assign(setStateObject, {
              shippingCountryObject,
            });

            const divisions = await handleFetchDivisions(contact.country);
            _.assign(setStateObject, {
              shippingDivisions: divisions,
            });

            const shippingStateObjectResult = _.find(divisions, { value: contact.state });
            if (shippingStateObjectResult) {
              _.assign(setStateObject, {
                shippingStateObject: {
                  label: shippingStateObjectResult.label,
                  value: shippingStateObjectResult.value,
                },
              });
            }

            // If returning address same as shipping address
            if (state.isSameAsShippingAddress) {
              const divisions = await handleFetchDivisions(contact.country);

              _.assign(setStateObject, {
                returningDivisions: divisions,
              });
            }
          } else {
            shippingCountryObject = _.find(
              countries,
              (x) => x.value === contact.country || _.includes(x.label, contact.country),
            );
            _.assign(setStateObject, {
              shippingCountryObject,
            });
          }

          // If returning address same as shipping address
          if (state.isSameAsShippingAddress) {
            _.assign(setStateObject, {
              shippingAddressData: updatedShippingAddressData,
              returningAddressData: updatedShippingAddressData,
              returningCountryObject: shippingCountryObject,
            });
          } else {
            //* Assign data for returning address when changing facility
            const divisions = await handleFetchDivisions(updatedReturningAddressData.country);

            _.assign(setStateObject, {
              shippingAddressData: updatedShippingAddressData,
              returningCountryObject: {
                label: updatedReturningAddressData.country === 'US' ? 'United States' : 'Canada',
                value: updatedReturningAddressData.country,
              },
              returningDivisions: divisions,
              returningAddressData: updatedReturningAddressData,
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch facility: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }

    setState(setStateObject);
  };

  /**
   * Populates the RMA shipping and returning address forms from existing address records. Maps each record onto the initial address shape and, for US or CA, sets the country option and loads its divisions to resolve the matching state option, otherwise matching the country by code or label in the country list, then applies everything in one state update marked as loaded.
   *
   * @param shippingAddress - Stored shipping address to map into the form.
   * @param returningAddress - Stored returning address to map into the form.
   * @returns Resolves once both addresses and their options are stored.
   */
  const loadAddressData = async (shippingAddress: any, returningAddress: any) => {
    const setStateObject = { isLoadedData: true };
    if (shippingAddress) {
      const { city, country, email, phone, state, street1, zip, name } = shippingAddress;

      const updatedShippingAddressData = _.cloneDeep(initialAddress);
      updatedShippingAddressData.name = name;
      updatedShippingAddressData.address = street1;
      updatedShippingAddressData.country = country || 'US';
      updatedShippingAddressData.city = city;
      updatedShippingAddressData.state = state;
      updatedShippingAddressData.zip = zip;
      updatedShippingAddressData.phoneNumber = phone;
      updatedShippingAddressData.email = email;

      if (country === 'US' || country === 'CA') {
        _.assign(setStateObject, {
          shippingCountryObject: {
            label: country === 'US' ? 'United States' : 'Canada',
            value: country,
          },
        });

        const divisions = await handleFetchDivisions(country);
        _.assign(setStateObject, {
          shippingDivisions: divisions,
        });

        const shippingStateObjectResult = _.find(divisions, { value: state });
        if (shippingStateObjectResult) {
          _.assign(setStateObject, {
            shippingStateObject: {
              label: shippingStateObjectResult.label,
              value: shippingStateObjectResult.value,
            },
          });
        }
      } else {
        _.assign(setStateObject, {
          shippingCountryObject: _.find(
            countries,
            (x) => x.value === country || _.includes(x.label, country),
          ),
        });
      }
      _.assign(setStateObject, {
        shippingAddressData: updatedShippingAddressData,
      });
    }
    if (returningAddress) {
      const { city, country, email, phone, state, street1, zip, name } = returningAddress;

      const updatedReturningAddressData = _.cloneDeep(initialAddress);
      updatedReturningAddressData.name = name;
      updatedReturningAddressData.address = street1;
      updatedReturningAddressData.country = country || 'US';
      updatedReturningAddressData.city = city;
      updatedReturningAddressData.state = state;
      updatedReturningAddressData.zip = zip;
      updatedReturningAddressData.phoneNumber = phone;
      updatedReturningAddressData.email = email;

      if (country === 'US' || country === 'CA') {
        _.assign(setStateObject, {
          returningCountryObject: {
            label: country === 'US' ? 'United States' : 'Canada',
            value: country,
          },
        });

        const divisions = await handleFetchDivisions(country);
        _.assign(setStateObject, {
          returningDivisions: divisions,
        });

        const returningStateObjectResult = _.find(divisions, { value: state });
        if (returningStateObjectResult) {
          _.assign(setStateObject, {
            returningStateObject: {
              label: returningStateObjectResult.label,
              value: returningStateObjectResult.value,
            },
          });
        }
      } else {
        _.assign(setStateObject, {
          returningCountryObject: _.find(
            countries,
            (x) => x.value === country || _.includes(x.label, country),
          ),
        });
      }
      _.assign(setStateObject, {
        returningAddressData: updatedReturningAddressData,
      });
    }

    setState(setStateObject);
  };

  /**
   * Writes a single field edit into the RMA shipping or returning address. Copies the target address object with the new value and mirrors the change into the returning address when it is marked same as shipping.
   *
   * @param name - Address field being edited.
   * @param value - New value for that field.
   * @param type - Which address to update, shipping or returning.
   * @param _id - Unused identifier accepted for the shared change signature.
   * @returns Nothing.
   */
  const onChangeDataAutoComplete = (name: any, value: any, type: any, _id?: any) => {
    if (type === 'shipping') {
      const updatedShippingAddressData = { ...state.shippingAddressData };
      updatedShippingAddressData[name] = value;
      setState({ shippingAddressData: updatedShippingAddressData });

      // If returning address same as shipping address
      if (state.isSameAsShippingAddress) {
        const updatedReturningAddressData = { ...state.returningAddressData };
        updatedReturningAddressData[name] = value;
        setState({ returningAddressData: updatedReturningAddressData });
      }
    } else if (type === 'returning') {
      const updatedReturningAddressData = { ...state.returningAddressData };
      updatedReturningAddressData[name] = value;
      setState({ returningAddressData: updatedReturningAddressData });
    }
  };

  /**
   * Applies a country or state dropdown selection to an RMA address. Choosing a country loads divisions for US or CA, blanks street, city, state, zip and phone and resets the state option, choosing a state records the option for US and CA only, and the whole change is mirrored into the returning address while it is same as shipping.
   *
   * @param name - Which selection changed, country or state.
   * @param option - Selected dropdown option with its label and value.
   * @param type - Which address to update, shipping or returning.
   * @returns Resolves once the selection and any loaded divisions are stored.
   */
  const onChangeSelectionAutoComplete = async (name: any, option: any = {}, type = '') => {
    const setStateObject = {};
    if (type === 'shipping') {
      const updatedShippingAddressData = { ...state.shippingAddressData };

      if (name === 'country') {
        if (option.value === 'US' || option.value === 'CA') {
          const countryCode = option.value;
          const divisions = await handleFetchDivisions(countryCode);
          _.assign(setStateObject, { shippingDivisions: divisions });
        }

        updatedShippingAddressData.address = '';
        updatedShippingAddressData.city = '';
        updatedShippingAddressData.state = '';
        updatedShippingAddressData.zip = '';
        updatedShippingAddressData.phoneNumber = '';

        _.assign(setStateObject, {
          shippingStateObject: null,
          shippingCountryObject: _.isEmpty(option) ? null : option,
        });
      } else if (name === 'state') {
        if (
          state.shippingAddressData.country === 'US' ||
          state.shippingAddressData.country === 'CA'
        ) {
          _.assign(setStateObject, {
            shippingStateObject: {
              label: option.label,
              value: option.value,
            },
          });
        }
      }

      updatedShippingAddressData[name] = option.value;
      _.assign(setStateObject, { shippingAddressData: updatedShippingAddressData });

      // If returning address same as shipping address
      if (state.isSameAsShippingAddress) {
        const updatedReturningAddressData = { ...state.returningAddressData };

        if (name === 'country') {
          if (option.value === 'US' || option.value === 'CA') {
            const countryCode = option.value;
            const divisions = await handleFetchDivisions(countryCode);
            _.assign(setStateObject, { returningDivisions: divisions });
          }

          updatedReturningAddressData.address = '';
          updatedReturningAddressData.city = '';
          updatedReturningAddressData.state = '';
          updatedReturningAddressData.zip = '';
          updatedReturningAddressData.phoneNumber = '';

          _.assign(setStateObject, {
            returningStateObject: null,
            returningCountryObject: _.isEmpty(option) ? null : option,
          });
        } else if (name === 'state') {
          if (
            state.returningAddressData.country === 'US' ||
            state.returningAddressData.country === 'CA'
          ) {
            _.assign(setStateObject, {
              returningStateObject: {
                label: option.label,
                value: option.value,
              },
            });
          }
        }

        updatedReturningAddressData[name] = option.value;
        _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
      }
    } else {
      const updatedReturningAddressData = { ...state.returningAddressData };

      if (name === 'country') {
        // returningAddressRef.current.resetData();
        // returningCityRef.current.resetData();

        if (option.value === 'US' || option.value === 'CA') {
          const countryCode = option.value;
          const divisions = await handleFetchDivisions(countryCode);
          _.assign(setStateObject, { returningDivisions: divisions });
        }

        updatedReturningAddressData.address = '';
        updatedReturningAddressData.city = '';
        updatedReturningAddressData.state = '';
        updatedReturningAddressData.zip = '';
        updatedReturningAddressData.phoneNumber = '';

        _.assign(setStateObject, {
          returningStateObject: null,
          returningCountryObject: _.isEmpty(option) ? null : option,
        });
      }

      //* Change state of returningAddressData
      if (name === 'state') {
        if (
          state.returningAddressData.country === 'US' ||
          state.returningAddressData.country === 'CA'
        ) {
          _.assign(setStateObject, {
            returningStateObject: {
              label: option.label,
              value: option.value,
            },
          });
        }
      }

      updatedReturningAddressData[name] = option.value;
      _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
    }
    setState(setStateObject);
  };

  /**
   * Empties the city, state and zip of an RMA address when an autocomplete selection is discarded. Clears those fields plus the street address unless the city search triggered it, resets the state option for the shipping or returning address, and mirrors the change into the returning address while it is marked same as shipping.
   *
   * @param isSearchCity - True when the city field drove the search, so the street address is kept.
   * @param type - Which address to clear, shipping or returning.
   * @returns Nothing.
   */
  const clearAutoComplete = (isSearchCity: any, type: any) => {
    if (type === 'shipping') {
      const updatedShippingAddressData = { ...state.shippingAddressData };

      updatedShippingAddressData.city = '';
      updatedShippingAddressData.state = '';
      updatedShippingAddressData.zip = '';
      if (!isSearchCity) {
        updatedShippingAddressData.address = '';
      }

      setState({
        shippingStateObject: null,
        shippingAddressData: updatedShippingAddressData,
      });

      // If returning address same as shipping address
      if (state.isSameAsShippingAddress) {
        const updatedReturningAddressData = { ...state.returningAddressData };

        updatedReturningAddressData.city = '';
        updatedReturningAddressData.state = '';
        updatedReturningAddressData.zip = '';
        if (!isSearchCity) {
          updatedReturningAddressData.address = '';
        }

        setState({
          returningStateObject: null,
          returningAddressData: updatedReturningAddressData,
        });
      }
    } else {
      const updatedReturningAddressData = { ...state.returningAddressData };

      updatedReturningAddressData.city = '';
      updatedReturningAddressData.state = '';
      updatedReturningAddressData.zip = '';
      if (!isSearchCity) {
        updatedReturningAddressData.address = '';
      }

      setState({
        returningStateObject: null,
        returningAddressData: updatedReturningAddressData,
      });
    }
  };

  /**
   * Records free typing in an address or city autocomplete input, debounced by 300 ms. Writes the value to the city field when the input is the city search and to the address field otherwise, mirroring it onto the returning address while that address is set to match shipping, then commits one combined state update.
   *
   * @param value - Text currently typed in the input.
   * @param isSearchCity - True when the input is the city search rather than the address line.
   * @param type - Which address block to update, shipping or returning.
   * @returns Nothing.
   */
  const onChangeInputAutoComplete = _.debounce((value, isSearchCity, type) => {
    const setStateObject = {};

    if (type === 'shipping') {
      const updatedShippingAddressData = { ...state.shippingAddressData };
      if (!isSearchCity) {
        updatedShippingAddressData.address = value;
      } else {
        updatedShippingAddressData.city = value;
      }

      _.assign(setStateObject, { shippingAddressData: updatedShippingAddressData });

      // If returning address same as shipping address
      if (state.isSameAsShippingAddress) {
        const updatedReturningAddressData = { ...state.returningAddressData };
        if (!isSearchCity) {
          updatedReturningAddressData.address = value;
        } else {
          updatedReturningAddressData.city = value;
        }
        _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
      }
    } else {
      const updatedReturningAddressData = { ...state.returningAddressData };
      if (!isSearchCity) {
        updatedReturningAddressData.address = value;
      } else {
        updatedReturningAddressData.city = value;
      }
      _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
    }

    setState(setStateObject);
  }, 300);

  /**
   * Fills an RMA address from a Google Places autocomplete result. Applies the returned street, city, state code and zip to the shipping or returning address, reloads divisions when the country changed, keeps the state option only for US and CA addresses, and repeats the same mapping on the returning address while it is same as shipping.
   *
   * @param data - Autocomplete result carrying country, address, city, state and zip.
   * @param isSearchCity - True when the city field drove the search, so the street address is kept.
   * @param type - Which address to fill, shipping or returning.
   * @returns Resolves once the address and its options are stored.
   */
  const onChangeAutoComplete = async (data: any, isSearchCity: any, type: any) => {
    const {
      country: autoCompleteCountry,
      address: autoCompleteAddress,
      city: autoCompleteCity,
      state: autoCompleteState,
      zip: autoCompleteZip,
    } = data;

    const setStateObject = {};

    if (type === 'shipping') {
      const updatedShippingAddressData = { ...state.shippingAddressData };
      if (!isSearchCity) {
        updatedShippingAddressData.address = autoCompleteAddress;
      }
      updatedShippingAddressData.city = autoCompleteCity;
      updatedShippingAddressData.state = autoCompleteState?.code;
      updatedShippingAddressData.zip = autoCompleteZip;

      if (autoCompleteCountry) {
        const autoCompleteCountryCode = autoCompleteCountry?.code;

        if (autoCompleteCountryCode) {
          if (autoCompleteCountryCode !== updatedShippingAddressData.country) {
            updatedShippingAddressData.country = {
              label: autoCompleteCountry.name,
              value: autoCompleteCountry.code,
            };

            const divisions = await handleFetchDivisions(autoCompleteCountryCode);
            _.assign(setStateObject, { shippingDivisions: divisions });
          }

          if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
            _.assign(setStateObject, {
              shippingStateObject: {
                label: autoCompleteState.name,
                value: autoCompleteState.code,
              },
            });
          } else {
            _.assign(setStateObject, { shippingStateObject: null });
          }
        }
      }

      _.assign(setStateObject, { shippingAddressData: updatedShippingAddressData });

      // If returning address same as shipping address
      if (state.isSameAsShippingAddress) {
        const updatedReturningAddressData = { ...state.returningAddressData };
        if (!isSearchCity) {
          updatedReturningAddressData.address = autoCompleteAddress;
        }
        updatedReturningAddressData.city = autoCompleteCity;
        updatedReturningAddressData.state = autoCompleteState?.code;
        updatedReturningAddressData.zip = autoCompleteZip;

        if (autoCompleteCountry) {
          const autoCompleteCountryCode = autoCompleteCountry?.code;

          if (autoCompleteCountryCode) {
            if (autoCompleteCountryCode !== updatedReturningAddressData.country) {
              updatedReturningAddressData.country = {
                label: autoCompleteCountry.name,
                value: autoCompleteCountry.code,
              };

              const divisions = await handleFetchDivisions(autoCompleteCountryCode);
              _.assign(setStateObject, { returningDivisions: divisions });
            }

            if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
              _.assign(setStateObject, {
                returningStateObject: {
                  label: autoCompleteState.name,
                  value: autoCompleteState.code,
                },
              });
            } else {
              _.assign(setStateObject, { returningStateObject: null });
            }
          }
        }

        _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
      }
    } else {
      const updatedReturningAddressData = { ...state.returningAddressData };
      if (!isSearchCity) {
        updatedReturningAddressData.address = autoCompleteAddress;
      }
      updatedReturningAddressData.city = autoCompleteCity;
      updatedReturningAddressData.state = autoCompleteState?.code;
      updatedReturningAddressData.zip = autoCompleteZip;

      if (autoCompleteCountry) {
        const autoCompleteCountryCode = autoCompleteCountry?.code;

        if (autoCompleteCountryCode) {
          if (autoCompleteCountryCode !== updatedReturningAddressData.country) {
            updatedReturningAddressData.country = {
              label: autoCompleteCountry.name,
              value: autoCompleteCountry.code,
            };

            const divisions = await handleFetchDivisions(autoCompleteCountryCode);
            _.assign(setStateObject, { returningDivisions: divisions });
          }

          if (autoCompleteCountryCode === 'US' || autoCompleteCountryCode === 'CA') {
            _.assign(setStateObject, {
              returningStateObject: {
                label: autoCompleteState.name,
                value: autoCompleteState.code,
              },
            });
          } else {
            _.assign(setStateObject, { returningStateObject: null });
          }
        }
      }

      _.assign(setStateObject, { returningAddressData: updatedReturningAddressData });
    }

    setState(setStateObject);
  };

  const idArr = [
    'shippingCountry',
    'shippingAddress',
    'shippingCity',
    'shippingState',
    'shippingZip',
    'shippingContactPhone',
    'shippingContactEmail',
    'returningCountry',
    'returningAddress',
    'returningCity',
    'returningState',
    'returningZip',
    'returningContactPhone',
    'returningContactEmail',
  ];

  /**
   * Moves focus to the next RMA address field when Enter is pressed. Locates the current input in the ordered id list and focuses the following id when both ids pass the null check, deliberately skipping preventDefault so react-select can still confirm an option with Enter.
   *
   * @param e - Keyboard event from an address field.
   * @returns Nothing.
   */
  const handleKeyDown = (e: any) => {
    const idIndex = _.indexOf(idArr, e.target.id) !== -1 ? _.indexOf(idArr, e.target.id) : null;
    if (
      idIndex !== null &&
      e.key === 'Enter' &&
      checkNullId(idArr[idIndex]) &&
      checkNullId(idArr[idIndex + 1])
    ) {
      // *: Do not use preventDefault to allow react select input select option when pressing Enter
      // e.preventDefault();
      document.getElementById(idArr[idIndex + 1])?.focus();
    }
  };

  useEffect(() => {
    if (state.isSameAsShippingAddress) {
      //* Clone data from shipping to returning
      const setStateObject = {
        returningCountryObject: state.shippingCountryObject,
        returningStateObject: state.shippingStateObject,
        returningDivisions: state.shippingDivisions,
      };
      if (!_.isEqual(state.returningAddressData, state.shippingAddressData)) {
        _.assign(setStateObject, {
          returningAddressData: state.shippingAddressData,
        });
      }
      setState(setStateObject);
    }
  }, [state.isSameAsShippingAddress]);

  useEffect(() => {
    //* Handle bug miss assign prop to state when component created
    if (isSameAsShippingAddress) {
      setState({ isSameAsShippingAddress: true });
    }
  }, [isSameAsShippingAddress]);

  //* Set default address data when changing supportFacilities
  //* Load data initial
  useEffect(() => {
    if (facilityId && countries) {
      setState({ isLoadedData: false });
      fetchFacilityData();
    }
  }, [facilityId, countries]);

  //* Load data from parent
  useEffect(() => {
    if (initialShippingAddress || initialReturningAddress) {
      setState({ isLoadedData: false });
      loadAddressData(initialShippingAddress, initialReturningAddress);
    }
  }, [initialShippingAddress, initialReturningAddress, countries]);

  //* Load returningCountryObject, returningStateObject when shippingStateObject changed
  useEffect(() => {
    if (state.isSameAsShippingAddress) {
      setState({
        returningCountryObject: state.shippingCountryObject,
        returningStateObject: state.shippingStateObject,
        returningDivisions: state.shippingDivisions,
      });
    }
  }, [state.shippingStateObject]);

  useEffect(() => {
    onChangeDataAddress('senderContact', state.shippingAddressData);
    onChangeDataAddress('recipientContact', state.returningAddressData);
  }, [state.shippingAddressData, state.returningAddressData]);

  useEffect(() => {
    onChangeDivision('shippingDivisions', state.shippingDivisions);
    onChangeDivision(
      'returningDivisions',
      state.isSameAsShippingAddress ? state.shippingDivisions : state.returningDivisions,
    );
  }, [state.shippingDivisions, state.returningDivisions]);

  return {
    state,
    setState,
    handleKeyDown,
    onChangeSelectionAutoComplete,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    onChangeDataAutoComplete,
  };
};

export { useAutoCompleteAddress };
